import { useTranslation } from 'react-i18next';

const getLevelKey = (totalOrders: number): string => {
  if (totalOrders >= 50) return 'passport.levelLegend';
  if (totalOrders >= 20) return 'passport.levelPatron';
  if (totalOrders >= 10) return 'passport.levelRegular';
  return 'passport.levelExplorer';
};

const getLevelCodeRaw = (totalOrders: number): string => {
  if (totalOrders >= 50) return 'LEG';
  if (totalOrders >= 20) return 'PAT';
  if (totalOrders >= 10) return 'REG';
  return 'EXP';
};

const formatMRZDate = (): string => {
  const d = new Date();
  return `${d.getFullYear().toString().slice(2)}${String(d.getMonth() + 1).padStart(2, '0')}${String(d.getDate()).padStart(2, '0')}`;
};

interface PassportCoverProps {
  holderName: string;
  totalOrders: number;
  totalPoints: number;
  restaurants: { id: string; name: string; orders: number; lastOrder: string }[];
  isFlipped: boolean;
  onFlip: () => void;
  bestRestaurantName?: string | null;
  favoriteDishName?: string | null;
}

export default function PassportCover({
  holderName,
  totalOrders,
  totalPoints,
  restaurants,
  isFlipped,
  onFlip,
  bestRestaurantName,
  favoriteDishName,
}: PassportCoverProps) {
  const { t } = useTranslation();
  const levelName = t(getLevelKey(totalOrders));
  const empty = t('passport.coverEmpty');

  return (
    <div className="pp-cover-wrapper" onClick={onFlip}>
      <div className={`pp-cover-inner ${isFlipped ? 'pp-cover-inner--flipped' : ''}`}>

        {/* FRONT FACE */}
        <div className="pp-cover-front">
          <div className="pp-cover-filigree pp-cover-filigree--tl" />
          <div className="pp-cover-filigree pp-cover-filigree--tr" />
          <div className="pp-cover-filigree pp-cover-filigree--bl" />
          <div className="pp-cover-filigree pp-cover-filigree--br" />

          <div className="pp-cover-topbar">
            <span className="pp-cover-network">{t('passport.coverNetwork')}</span>
            <span className="pp-cover-level-badge"> {levelName.toUpperCase()}</span>
          </div>

          <div className="pp-cover-logo-wrap">
            <img src="/logo-olso.png" alt="OLSO" className="pp-cover-logo-img" />
          </div>

          <div className="pp-cover-title-label">FOOD PASSPORT</div>
          <div className="pp-cover-holder-name">{holderName}</div>

          <div className="pp-cover-stats">
            <div className="pp-cover-stat">
              <span className="pp-cover-stat-value">{totalOrders}</span>
              <span className="pp-cover-stat-label">{t('passport.coverStatOrders')}</span>
            </div>
            <div className="pp-cover-stat">
              <span className="pp-cover-stat-value">{totalPoints}</span>
              <span className="pp-cover-stat-label">{t('passport.coverStatPoints')}</span>
            </div>
            <div className="pp-cover-stat">
              <span className="pp-cover-stat-value">{restaurants.length}</span>
              <span className="pp-cover-stat-label">{t('passport.coverStatRestaurants')}</span>
            </div>
          </div>

          {/* MRZ zone */}
          <div className="pp-cover-mrz-zone">
            <div className="pp-cover-mrz-line">
              P&lt;OLSO&lt;{holderName.toUpperCase().replace(/\s/g, '<')}&lt;&lt;&lt;&lt;&lt;&lt;&lt;&lt;&lt;&lt;&lt;&lt;&lt;&lt;
            </div>
            <div className="pp-cover-mrz-line">
              FP{formatMRZDate()}&lt;&lt;{getLevelCodeRaw(totalOrders)}&lt;{totalOrders}&lt;{totalPoints}&lt;&lt;&lt;&lt;&lt;&lt;&lt;&lt;&lt;&lt;&lt;
            </div>
          </div>

          <div className="pp-cover-flip-hint">▶ {t('passport.tapToOpen')}</div>
        </div>

        {/* BACK FACE */}
        <div className="pp-cover-back">
          <div className="pp-cover-filigree pp-cover-filigree--tl" />
          <div className="pp-cover-filigree pp-cover-filigree--tr" />
          <div className="pp-cover-filigree pp-cover-filigree--bl" />
          <div className="pp-cover-filigree pp-cover-filigree--br" />

          <div className="pp-cover-back-header">{t('foodPassport.title')}</div>

          <div className="pp-cover-params">
            <div className="pp-cover-param-row">
              <span className="pp-cover-param-label">{t('passport.coverRank')}</span>
              <span className="pp-cover-param-value">{levelName}</span>
            </div>
            <div className="pp-cover-param-divider" />
            <div className="pp-cover-param-row">
              <span className="pp-cover-param-label">{t('passport.coverBestRestaurant')}</span>
              <span className="pp-cover-param-value">{bestRestaurantName || empty}</span>
            </div>
            <div className="pp-cover-param-divider" />
            <div className="pp-cover-param-row">
              <span className="pp-cover-param-label">{t('passport.coverFavoriteDish')}</span>
              <span className="pp-cover-param-value">{favoriteDishName || empty}</span>
            </div>
          </div>
        </div>

      </div>
    </div>
  );
}
